/**
 * TextCell — TEXT-variant editable cell. Accepts optional `isOnlyText` to
 * restrict keystrokes to letters / spaces / common punctuation (used for
 * contact-name columns where the backend enforces the same regex).
 */

import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { validateTextOnlyKey } from '../../utils/cellValidation';

import CellShell from './CellShell';
import type { BaseCellProps } from './types';
import { commitInputChange, hydrateFromValue } from './inputCellBehavior';

interface TextCellProps extends BaseCellProps {
  isOnlyText?: boolean;
  maxCount?: number;
}

const TextCell = ({
  value,
  id,
  name,
  isActive,
  isDisabled = false,
  isOptionalField = false,
  errorMessage,
  setData,
  isOnlyText = false,
  maxCount,
}: TextCellProps) => {
  const [inputText, setInputText] = useState(() => hydrateFromValue(value));

  useEffect(() => {
    setInputText(hydrateFromValue(value));
  }, [value]);

  const handleBlur = (inputValue: string): void => {
    commitInputChange({
      inputValue,
      originalValue: value,
      id,
      name,
      isActive,
      errorMessage,
      isOptionalField,
      setData,
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (isOnlyText) validateTextOnlyKey(e);
  };

  return (
    <CellShell
      isActive={isActive}
      isOptionalField={isOptionalField}
      errorMessage={errorMessage}
      value={value}
    >
      <Input
        id="input-value"
        type="text"
        aria-describedby="input-value"
        name="input"
        placeholder={value === '-' ? '-' : value === null ? '-' : undefined}
        disabled={isDisabled}
        value={inputText}
        maxLength={maxCount}
        className={cn(isActive && 'border-destructive')}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        onKeyDown={handleKeyDown}
        data-testid="input-value_id"
      />
    </CellShell>
  );
};

export default TextCell;
